import { getApiUrl } from '../config/api';

export interface InventoryItem {
  id: number;
  inventory_id: string;
  supplier_id: number;
  date_added: string;
  category: string;
  phone_model: string;
  imei_mac_address: string;
  buying_price: number;
  service_expense: number;
  package_expense: number;
  selling_price: number;
  branch_id: number;
  acquisition_notes?: string;
  payment_method: string;
  status: string;
  created_at: string;
  updated_at: string;
  suppliers: {
    id: number;
    name: string;
    phone_number?: string;
    aadhar?: string;
    address?: string;
  };
  branches: {
    id: number;
    branch_name: string;
  };
}

export interface InventorySearchOptions {
  searchTerm: string;
  statusFilter?: 'ready' | 'sold' | 'service' | 'reserved';
  branchId?: string;
  excludeCartItems?: any[];
  userRole?: string;
  isAllBranches?: boolean;
}

export interface InventorySearchResult {
  success: boolean;
  items: InventoryItem[];
  total: number;
  error?: string;
}

/**
 * Unified inventory search service for all components
 * Handles exact inventory ID matching and partial search
 */
export class InventorySearchService {
  /**
   * Search inventory items with unified logic
   */
  static async searchInventory(
    token: string,
    options: InventorySearchOptions
  ): Promise<InventorySearchResult> {
    const {
      searchTerm,
      statusFilter = 'ready',
      branchId,
      excludeCartItems = [],
      userRole,
      isAllBranches = true
    } = options;

    console.log('🔍 InventorySearchService: Starting search');
    console.log('🔎 Search term:', searchTerm);
    console.log('👤 User role:', userRole);
    console.log('🏢 Branch settings:', { branchId, isAllBranches });
    console.log('🟢 Status filter:', statusFilter);

    if (!token || !searchTerm.trim()) {
      return {
        success: false,
        items: [],
        total: 0,
        error: 'Missing token or search term'
      };
    }

    try {
      const params = new URLSearchParams();
      params.append('status_filter', statusFilter);
      params.append('q', searchTerm.trim());

      // Apply branch filtering based on user role
      if (userRole === 'branch_manager') {
        if (!isAllBranches && branchId) {
          params.append('branch_id', branchId);
        }
      } else {
        // For admins and inventory managers, respect branch selection
        if (!isAllBranches && branchId) {
          params.append('branch_id', branchId);
        }
      }

      const apiUrl = `${getApiUrl('inventory')}?${params}`;
      console.log('📡 API URL:', apiUrl);
      console.log('📋 URL params:', params.toString());

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📨 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ API Error response:', errorText);
        return {
          success: false,
          items: [],
          total: 0,
          error: 'Failed to search inventory'
        };
      }

      const data = await response.json();
      console.log('📦 API Response:', data);

      if (!data.success) {
        console.log('❌ API returned success: false');
        return {
          success: false,
          items: [],
          total: 0,
          error: data.message || 'Search failed'
        };
      }

      // Get inventory items from response (handle different response formats)
      const inventoryItems = data.data || data.inventory || [];
      
      // Filter out items already in cart if provided
      const availableItems = excludeCartItems.length > 0
        ? inventoryItems.filter((item: InventoryItem) =>
            !excludeCartItems.some(cartItem => cartItem.inventory_item?.id === item.id || cartItem.id === item.id)
          )
        : inventoryItems;

      console.log('✅ Available items after cart filter:', availableItems.length);
      console.log('📋 Items:', availableItems.map((item: InventoryItem) => ({
        id: item.inventory_id,
        status: item.status,
        model: item.phone_model
      })));

      return {
        success: true,
        items: availableItems,
        total: availableItems.length
      };

    } catch (error) {
      console.error('❌ InventorySearchService error:', error);
      return {
        success: false,
        items: [],
        total: 0,
        error: 'An error occurred while searching'
      };
    }
  }

  /**
   * Check if a search term matches inventory ID format
   */
  static isInventoryIdFormat(searchTerm: string): boolean {
    return /^IH-INV-\d{8}$/.test(searchTerm.trim());
  }

  /**
   * Get available inventory for sales (status = 'ready')
   */
  static async getAvailableInventory(
    token: string,
    searchTerm: string,
    options: Omit<InventorySearchOptions, 'searchTerm' | 'statusFilter'> = {}
  ): Promise<InventorySearchResult> {
    return this.searchInventory(token, {
      ...options,
      searchTerm,
      statusFilter: 'ready'
    });
  }
}